import { useMemo, useState } from 'react';
import { LessonHeader } from './LessonHeader';
import { CheckIcon, CrossIcon, screenShell } from './common';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { WEB_ICO } from '../../shell/icons';
import { useAsync } from '../../hooks/useAsync';
import { contentApi } from '../../api/content';
import type { LessonScreenProps } from './types';

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export function VocabQuizScreen({ lessonId, unitName, lessonName, onBack, onNext }: LessonScreenProps) {
  const lesson = useAsync(() => contentApi.getLesson(lessonId), [lessonId]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);

  const items = useMemo(() => (lesson.data ? lesson.data.items.filter((it) => it.itemType === 'VOCAB') : []), [lesson.data]);
  const item = items[index];

  const options = useMemo(() => {
    if (!item || item.itemType !== 'VOCAB') return [];
    const pool = items.filter((it) => it.itemType === 'VOCAB' && it.id !== item.id).map((it) => (it.itemType === 'VOCAB' ? it.meaningVi : ''));
    const distractors = shuffle(pool.filter(Boolean)).slice(0, 3);
    return shuffle([item.meaningVi, ...distractors]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (lesson.loading) return <LoadingState />;
  if (lesson.error || !lesson.data) return <ErrorState message="Không tải được bài học." />;
  if (!item || item.itemType !== 'VOCAB') return <ErrorState message="Bài học này chưa có nội dung từ vựng." />;

  const isCorrect = picked === item.meaningVi;

  const next = () => {
    if (index < items.length - 1) {
      setIndex(index + 1);
      setPicked(null);
    } else {
      onNext();
    }
  };

  return (
    <div style={screenShell}>
      <LessonHeader onBack={onBack} ctx={{ track: 'vocab', unit: unitName, lesson: lessonName, step: 2, total: 2 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontFamily: 'var(--font-jp)' }}>
          <span style={{ fontSize: 48, fontWeight: 400, color: 'var(--ink)' }}>{item.kanji ?? item.kana}</span>
          {item.kanji && <span style={{ fontSize: 22, color: 'var(--text-secondary)' }}>{`（${item.kana}）`}</span>}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>Chọn nghĩa đúng</div>
        {picked && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: isCorrect ? 'var(--success)' : 'var(--error)',
              color: '#fff',
              borderRadius: 999,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {isCorrect ? <CheckIcon /> : <CrossIcon />}
            {isCorrect ? 'Đúng rồi!' : 'Chưa đúng'}
          </div>
        )}
      </div>

      <div style={{ padding: '0 20px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {options.map((opt) => {
            const isThisCorrect = opt === item.meaningVi;
            let bg = 'var(--surface-page)';
            let border = 'var(--border-default)';
            if (picked && isThisCorrect) {
              bg = 'rgba(46,139,87,0.12)';
              border = 'var(--success)';
            } else if (picked && opt === picked && !isCorrect) {
              bg = 'var(--error-bg)';
              border = 'var(--error)';
            }
            return (
              <button
                key={opt}
                onClick={() => !picked && setPicked(opt)}
                style={{
                  minHeight: 96,
                  padding: '12px 10px',
                  borderRadius: 'var(--radius-card)',
                  border: `2px solid ${border}`,
                  background: bg,
                  cursor: picked ? 'default' : 'pointer',
                  fontFamily: 'var(--font-vi)',
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'var(--ink)',
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: '1px solid var(--border-default)' }}>
        <button
          onClick={() => {
            if (index > 0) {
              setIndex(index - 1);
              setPicked(null);
            }
          }}
          disabled={index === 0}
          aria-label="Câu trước"
          style={{
            width: 64,
            height: 52,
            borderRadius: 'var(--radius-control)',
            border: '1.5px solid var(--border-default)',
            background: 'var(--surface-page)',
            cursor: index === 0 ? 'not-allowed' : 'pointer',
            opacity: index === 0 ? 0.4 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {WEB_ICO.back('var(--ink)', 20)}
        </button>
        <button
          onClick={next}
          disabled={!picked}
          style={{
            flex: 1,
            height: 52,
            borderRadius: 'var(--radius-control)',
            background: 'var(--cta-default)',
            color: '#fff',
            border: 'none',
            cursor: picked ? 'pointer' : 'not-allowed',
            opacity: picked ? 1 : 0.45,
            fontFamily: 'var(--font-vi)',
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          Tiếp theo
        </button>
      </div>
    </div>
  );
}
